// Shared split-screen auth layout (left clinician pitch + right card). Used by SignIn,
// CreateAccount, ResetPassword and CheckEmail so the prototype's split-screen is consistent.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Brand } from "@championcare/ui";

const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const FEATURES = [
  "One-click Zoom from any scheduled visit",
  "Thin patient briefs — the full chart stays in the EMR",
  "Secure messaging built for clinical use",
  "Calendar control — block time, set availability",
];

export function AuthLayout({
  tab,
  variant = "card",
  children,
}: {
  tab?: "signin" | "create";
  // "card" wraps children in the bespoke .auth-card (used by the auth forms). "plain" drops the card
  // chrome so a self-contained component that renders its own card can sit in a clean centered
  // column with its own masthead — matching the patient/operator sign-in (THU-357).
  variant?: "card" | "plain";
  children: ReactNode;
}) {
  return (
    <div className="auth-screen">
      <div className="auth-left">
        <div className="auth-top">
          <span className="badge">For Clinicians</span>
        </div>
        <div className="auth-pitch">
          <p className="eyebrow">ChampionCare Provider Portal</p>
          <h1>
            Care that champions <em>your</em> practice.
          </h1>
          <p>
            The platform built for physicians who refuse to compromise on quality time with their
            patients. Schedule, message, and meet — all from one place.
          </p>
          <div className="auth-features">
            {FEATURES.map((f) => (
              <div key={f} className="auth-feature">
                <span className="dot">{CHECK}</span>
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div />
      </div>

      <div className="auth-right">
        {variant === "plain" ? (
          <div className="auth-right-plain">{children}</div>
        ) : (
          <div className="auth-card">
            <Brand size={22} />
            {tab && (
              <div className="auth-tabs">
                <Link to="/sign-in" className={`auth-tab ${tab === "signin" ? "active" : ""}`}>
                  Sign in
                </Link>
                <Link to="/create-account" className={`auth-tab ${tab === "create" ? "active" : ""}`}>
                  Create account
                </Link>
              </div>
            )}
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
